<template>
    <div class="pregnancy-compact">
        <!-- Container compacto con toda la información -->
        <div class="pregnancy-container">
            <!-- Fila superior: Toggle + FUR -->
            <div class="pregnancy-header">
                <!-- Toggle con label -->
                <div class="control-toggle-compact">
                    <label class="toggle-label">
                        <input v-model="isPregnant" type="checkbox" @change="toggleCalculation">
                        <span class="toggle-slider"></span>
                    </label>
                    <span class="control-text">Control de embarazo</span>
                </div>

                <!-- Campo FUR (visible si está activo) -->
                <transition name="fade">
                    <div v-if="isPregnant" class="fur-compact">
                        <label>FUR</label>
                        <DatePicker
v-model="lastMenstrualPeriod" :errors="errors" error-field-name="fur"
                            @change="calculatePregnancyDetails" />
                    </div>
                </transition>
            </div>

            <!-- Resultados centrados (solo visibles si hay cálculo) -->
            <transition name="slide-fade">
                <div v-if="gestationalAge" class="results-centered">
                    <div class="result-item">
                        <i class="fas fa-hourglass-half"></i>
                        <span>{{ gestationalAge }}</span>
                    </div>
                    <div class="result-divider"></div>
                    <div class="result-item">
                        <i class="fas fa-calendar-check"></i>
                        <span>FPP: {{ formatDate(dueDate) }}</span>
                    </div>
                    <div class="result-divider"></div>
                    <div class="result-item">
                        <i class="fas fa-chart-line"></i>
                        <span>{{ trimester }}</span>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import DatePicker from "@/components/DatePicker.vue";
import api from '@/services/api';
import debounce from 'lodash.debounce';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    },
    data: {
        type: Object,
        default: () => ({})
    }
});

const isPregnant = ref(false);
const lastMenstrualPeriod = ref('');
const gestationalAge = ref('');
const dueDate = ref('');
const trimester = ref('');
const updated_at = ref(new Date().toISOString());
const errors = ref({});
const isInitialLoad = ref(true);

const toggleCalculation = () => {
    if (!isPregnant.value) {
        // Al desactivar, limpiar todos los campos
        lastMenstrualPeriod.value = '';
        gestationalAge.value = '';
        dueDate.value = '';
        trimester.value = '';
    } else {
        // Al activar, calcular si hay fecha FUR
        if (lastMenstrualPeriod.value) {
            calculatePregnancyDetails();
        }
    }
    // Guardar el cambio (sin esperar al debounce)
    if (!isInitialLoad.value) {
        savePregnancyData();
    }
};

const calculatePregnancyDetails = () => {
    // Solo calcular si está embarazada y hay fecha FUR
    if (!isPregnant.value || !lastMenstrualPeriod.value) {
        gestationalAge.value = '';
        dueDate.value = '';
        trimester.value = '';
        return;
    }

    // Crear fecha en zona horaria local para evitar problemas de desfase
    const [year, month, day] = lastMenstrualPeriod.value.split('-').map(Number);
    const furDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Resetear hora para comparación precisa
    
    const diffTime = Math.abs(today - furDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7);
    const days = diffDays % 7;

    gestationalAge.value = `${weeks} sem ${days} días`;

    // Calcular FPP (280 días desde la FUR) en zona horaria local
    const dueDateCalc = new Date(year, month - 1, day);
    dueDateCalc.setDate(dueDateCalc.getDate() + 280);
    
    // Formatear fecha manualmente para evitar conversión de zona horaria
    const dueYear = dueDateCalc.getFullYear();
    const dueMonth = String(dueDateCalc.getMonth() + 1).padStart(2, '0');
    const dueDay = String(dueDateCalc.getDate()).padStart(2, '0');
    dueDate.value = `${dueYear}-${dueMonth}-${dueDay}`;

    if (weeks < 13) {
        trimester.value = '1er Trimestre';
    } else if (weeks < 27) {
        trimester.value = '2do Trimestre';
    } else {
        trimester.value = '3er Trimestre';
    }
};

const formatDate = (date) => {
    if (!date) return '';
    // Parsear fecha manualmente para evitar conversión de zona horaria
    const [year, month, day] = date.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    const formattedDay = String(d.getDate()).padStart(2, '0');
    const formattedMonth = String(d.getMonth() + 1).padStart(2, '0');
    const formattedYear = d.getFullYear();
    return `${formattedDay}/${formattedMonth}/${formattedYear}`;
};

const savePregnancyData = async () => {
    if (isInitialLoad.value) {
        isInitialLoad.value = false;
        return;
    }

    const pregnancyData = {
        gynecology: {
            is_pregnant: isPregnant.value ? 1 : 0,
            last_menstrual_period: lastMenstrualPeriod.value || null
        }
    };

    try {
        const response = await api.put(`/pacientes/${props.patientRecord.id}/gynecology`, pregnancyData);
        const gynecologyData = response.data.data.gynecology;
        
        if (gynecologyData.updated_at) {
            updated_at.value = gynecologyData.updated_at;
        }
        
        // Usar datos del backend si existen, sino calcular localmente
        if (gynecologyData.gestational_age) {
            gestationalAge.value = gynecologyData.gestational_age;
        }
        
        if (gynecologyData.due_date) {
            dueDate.value = gynecologyData.due_date;
        }
        
        if (gynecologyData.trimester) {
            trimester.value = gynecologyData.trimester;
        }
        
        // Si el backend no envió los datos calculados, calcular localmente
        if (!gynecologyData.gestational_age && isPregnant.value && lastMenstrualPeriod.value) {
            calculatePregnancyDetails();
        }
    } catch (error) {
        console.error('Error al guardar los datos de embarazo:', error);
    }
};

// Debounce la función savePregnancyData
const debouncedSavePregnancyData = debounce(savePregnancyData, 1000);

// Watch para guardar cambios
watch([
    isPregnant,
    lastMenstrualPeriod
], debouncedSavePregnancyData, { deep: true });

// Watch para calcular cuando cambia FUR
watch(lastMenstrualPeriod, (newValue) => {
    if (isPregnant.value && newValue) {
        calculatePregnancyDetails();
    }
});

// Watch para cargar datos iniciales desde el backend
watch(() => props.data, (newData) => {
    if (newData) {
        isPregnant.value = Boolean(newData.is_pregnant);
        lastMenstrualPeriod.value = newData.last_menstrual_period || '';

        if (newData.updated_at) {
            updated_at.value = newData.updated_at;
        }

        // Cargar datos calculados del backend si existen
        if (newData.gestational_age) {
            gestationalAge.value = newData.gestational_age;
        }
        if (newData.due_date) {
            dueDate.value = newData.due_date;
        }
        if (newData.trimester) {
            trimester.value = newData.trimester;
        }
        
        // Si el backend no envió datos calculados, calcular localmente
        if (!newData.gestational_age && isPregnant.value && lastMenstrualPeriod.value) {
            calculatePregnancyDetails();
        }
    }
}, { immediate: true });
</script>

<style scoped>
/* Container compacto */
.pregnancy-compact {
    margin-bottom: 0.75rem;
}

/* Container principal con borde */
.pregnancy-container {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 0.875rem 1rem;
    transition: all 0.3s ease;
    margin: 1.5rem 1.5rem;
}

.pregnancy-container:hover {
    border-color: #667eea;
    box-shadow: 0 2px 12px rgba(102, 126, 234, 0.1);
}

/* Fila superior: Toggle + FUR */
.pregnancy-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

/* Toggle compacto */
.control-toggle-compact {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.control-text {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #344767;
    letter-spacing: 0.01em;
    white-space: nowrap;
}

/* Toggle Switch */
.toggle-label {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    cursor: pointer;
    flex-shrink: 0;
}

.toggle-label input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #dee2e6 0%, #e9ecef 100%);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 24px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.toggle-label input:checked+.toggle-slider {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-label input:checked+.toggle-slider:before {
    transform: translateX(20px);
}

/* Campo FUR compacto a la derecha */
.fur-compact {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.fur-compact label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #667eea;
    white-space: nowrap;
}

/* Resultados centrados debajo */
.results-centered {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 0.875rem;
    padding-top: 0.875rem;
    border-top: 1px solid #e9ecef;
    flex-wrap: wrap;
}

.result-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #344767;
    white-space: nowrap;
}

.result-item i {
    font-size: 0.875rem;
    color: #667eea;
    flex-shrink: 0;
}

.result-divider {
    width: 1px;
    height: 18px;
    background: #dee2e6;
    flex-shrink: 0;
}

/* Transiciones */
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.25s ease-in;
}

.slide-fade-enter-from {
    transform: translateY(-10px);
    opacity: 0;
}

.slide-fade-leave-to {
    transform: translateY(-5px);
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
    .pregnancy-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
    }

    .fur-compact {
        width: 100%;
    }

    .results-centered {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.625rem;
    }

    .result-divider {
        display: none;
    }

    .result-item {
        width: 100%;
    }
}

@media (max-width: 480px) {
    .pregnancy-container {
        padding: 0.75rem 0.875rem;
    }

    .control-text {
        font-size: 0.875rem;
    }

    .result-item {
        font-size: 0.8125rem;
    }

    .results-centered {
        padding-top: 0.75rem;
        margin-top: 0.75rem;
    }
}
</style>
